import '../styles/pages/product.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailIcon from '@mui/icons-material/Email';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PhoneIcon from '@mui/icons-material/Phone';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import RemoveIcon from '@mui/icons-material/Remove';
import ShareIcon from '@mui/icons-material/Share';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion, AnimatePresence } from 'framer-motion';

import Loading from '../components/global/loading/Loading';
import ReadOnlyRichTextDisplay from '../components/global/ReadOnlyRichTextDisplay';
import ProudctAddedToList from '../components/modals/productAddedToList/ProudctAddedToList';
import { useProduct } from '../context/ProductContext';
import { useShoppingList } from '../context/ShoppingListContext';
import ProductQuestionaire from '../forms/InquiryForm';
import { Category } from '../models/Category';
import { Product } from '../models/Product';
import getIconForDeliveryMethod from '../helperFunctions/getIconForDeliveryMethod';
import { DeliveryMethod } from '../models/Order';
import translateDevlieryMethod from '../helperFunctions/translateDeliveryMethod';
import { useConfig } from '../utils/useConfig';
import ReviewSection from '../components/reviews/ReviewSection';
import SuggestedProducts from '../components/productSections/SuggestedProducts';
import Seo from '../components/seo/Seo';

function ProductPage() {
  const config = useConfig();

  const { name } = useParams();
  const { getProductByName, getProducts } = useProduct();

  const [newProducts, setNewProducts] = useState<Product[] | null>(null);
  const [product, setProduct] = React.useState<Product | null>(null);
  const [categoryAncestry, setCategoryAncestry] = React.useState<Category[]>(
    [],
  );
  const [cheapestDelivery, setCheapestDelivery] = React.useState<{
    deliveryCost: number;
    deliveryMethod: DeliveryMethod;
  } | null>(null);

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [productNotFound, setProductNotFound] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { addToShoppingList } = useShoppingList();

  const [productsNumber, setProductsNumber] = useState<number>(1);

  useEffect(() => {
    const fetchNewProducts = async () => {
      const response = await getProducts({
        order: 'desc',
        orderBy: 'date',
        limit: 5,
      });
      setNewProducts(response.products);
    };

    fetchNewProducts();
  }, [getProducts]);

  const handleShare = async () => {
    if (!product) {
      return;
    }

    if (navigator.share) {
      try {
        // Utworzenie danych do udostępnienia bez próby pobierania obrazu
        const shareData = {
          title: product.name,
          text: `${product.name}\nCena: ${product.price.toFixed(2)} zł brutto (${(product.price * 0.77).toFixed(2)} zł netto)`,
          url: window.location.href,
        };

        // Próbujemy dodać obraz tylko jeśli URL jest z tej samej domeny
        // lub jeśli domena obrazu ma skonfigurowane CORS
        if (
          product.mobilePrimaryImage &&
          (new URL(product.mobilePrimaryImage).origin ===
            window.location.origin ||
            product.mobilePrimaryImage.includes('data:image'))
        ) {
          try {
            const imageResponse = await fetch(product.mobilePrimaryImage);
            const imageBlob = await imageResponse.blob();
            const imageFile = new File([imageBlob], `${product.slug}.jpg`, {
              type: imageBlob.type,
            });
            // @ts-ignore - files nie jest standardową właściwością, ale jest obsługiwane przez nowoczesne przeglądarki
            shareData.files = [imageFile];
          } catch (imageError) {
            console.warn(
              'Nie udało się pobrać obrazu do udostępnienia:',
              imageError,
            );
            // Kontynuujemy bez obrazu
          }
        } else {
          console.warn(
            'Pominięto dodawanie obrazu ze względu na potencjalne problemy z CORS',
          );
        }

        await navigator.share(shareData);
      } catch (error) {
        console.error('Błąd podczas udostępniania produktu', error);
      }
    } else {
      handleCopya();
    }
  };

  const handleCopya = () => {
    if (!product) {
      return;
    }

    const shareText = `Check out this product: ${product.name}\n${window.location.href}\n\nImage:\n${product.primaryImage}`;

    navigator.clipboard.writeText(shareText);
  };

  React.useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);

      try {
        const response = await getProductByName(name as string);
        setProduct(response.product);
        // Odwróć tablicę tylko raz podczas pobierania danych
        setCategoryAncestry(response.categoryAncestry.slice());
        setCheapestDelivery(response.cheapestDelivery);
        setProductNotFound(false);
      } catch (error: any) {
        console.error('Błąd podczas ładowania produktu', error);
        setProductNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [getProductByName, name]);

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  if (isLoading) {
    return <Loading />;
  }

  // Renderuj komunikat o nie znalezionym produkcie
  if (productNotFound) {
    return (
      <>
        <motion.div
          className="mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-5xl"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div className="text-center mb-8" variants={itemVariants}>
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, type: 'spring' }}
            >
              <ErrorOutlineIcon
                className="text-gray-500"
                style={{ fontSize: 48 }}
              />
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Produkt nie został znaleziony
            </h1>
            <p className="text-gray-600 max-w-lg mx-auto">
              Przepraszamy, ale produkt o nazwie{' '}
              <span className="font-medium text-red-500">"{name}"</span> nie
              istnieje lub został usunięty z naszej bazy. Sprawdź poprawność
              adresu URL lub skorzystaj z wyszukiwarki.
            </p>
          </motion.div>

          <motion.div
            className="flex flex-wrap justify-center gap-4 mb-12"
            variants={itemVariants}
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <a
                href="/"
                className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-6 rounded-full transition-colors"
              >
                <HomeIcon fontSize="small" />
                <span>Strona główna</span>
              </a>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <a
                href="/szukaj"
                className="inline-flex items-center gap-2 bg-primary text-white font-medium py-3 px-6 rounded-full transition-colors hover:bg-background"
              >
                <SearchIcon fontSize="small" />
                <span>Wyszukaj produkty</span>
              </a>
            </motion.div>
          </motion.div>

          {/* Nowości produkty */}
          <AnimatePresence>
            {newProducts && newProducts.length > 0 && (
              <motion.div variants={itemVariants} className="mt-16">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Nowości
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {newProducts.map((newProduct, index) => (
                    <motion.div
                      key={newProduct.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                      whileHover={{ scale: 1.03, y: -4 }}
                      className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all h-full"
                    >
                      <a
                        href={`/produkt/${newProduct.slug}`}
                        className="h-full flex flex-col"
                      >
                        <div className="aspect-square bg-gray-50 p-4">
                          <img
                            src={newProduct.mobilePrimaryImage}
                            alt={newProduct.name}
                            className="w-full h-full object-contain mix-blend-multiply"
                          />
                        </div>
                        <div className="p-4 flex flex-col flex-grow">
                          <h3 className="text-sm font-medium text-gray-800 line-clamp-2 mb-2 hover:text-blue-600 transition-colors">
                            {newProduct.name}
                          </h3>
                          <div className="mt-auto">
                            <div className="flex items-baseline gap-2">
                              <p className="text-base font-bold text-gray-900">
                                {newProduct.price.toFixed(2)} zł
                              </p>
                              <span className="text-xs text-gray-500">
                                netto: {(newProduct.price * 0.77).toFixed(2)} zł
                              </span>
                            </div>
                          </div>
                        </div>
                      </a>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        <Seo
          title={`Produkt nie został znaleziony`}
          description="Przepraszamy, ale produkt o podanej nazwie nie został znaleziony. Sprawdź poprawność adresu URL lub skorzystaj z wyszukiwarki."
          image={config.company.logo}
          noindex={true}
        />
      </>
    );
  }

  if (!product) {
    return null; // lub inny komponent ładowania
  }

  // Reszta komponentu (istniejący kod dla znalezionego produktu)
  return (
    <>
      <motion.div
        className="mx-auto px-4 sm:px-6 lg:px-8 py-8"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Breadcrumbs */}
        <motion.nav
          variants={itemVariants}
          className="lg:flex mb-8 text-sm text-gray-500 hidden"
          aria-label="Breadcrumb"
        >
          <ol className="flex items-center space-x-2 flex-wrap">
            <li>
              <a
                href="/szukaj"
                className="hover:text-blue-600 transition-colors whitespace-nowrap"
              >
                Kategorie główne
              </a>
            </li>
            {categoryAncestry?.map((category, index) => (
              <React.Fragment key={category.id}>
                <li className="flex items-center whitespace-nowrap">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    />
                  </svg>
                  <a
                    href={`/szukaj?categoryId=${category.id}`}
                    className="ml-2 hover:text-blue-600 transition-colors"
                  >
                    {category.name}
                  </a>
                </li>
              </React.Fragment>
            ))}
          </ol>
        </motion.nav>

        {/* Nowa struktura z trzema kolumnami na dużych ekranach */}
        <div className="grid grid-cols-12 gap-6">
          {/* Lewy sidebar z nowymi produktami - tylko na dużych ekranach */}
          <motion.div
            className="hidden lg:block lg:col-span-2 space-y-6"
            variants={itemVariants}
          >
            <div className="sticky top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Nowości</h2>
              <div className="space-y-4">
                {newProducts ? (
                  newProducts.map((newProduct) => (
                    <motion.div
                      key={newProduct.id}
                      whileHover={{ y: -4 }}
                      className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all"
                    >
                      <a href={`/produkt/${newProduct.slug}`} className="block">
                        <div className="aspect-square bg-gray-50 p-2">
                          <img
                            src={newProduct.mobilePrimaryImage}
                            alt={newProduct.name}
                            className="w-full h-full object-contain mix-blend-multiply"
                          />
                        </div>
                        <div className="p-3">
                          <h3 className="text-sm font-medium text-gray-800 line-clamp-2 mb-1 hover:text-blue-600 transition-colors">
                            {newProduct.name}
                          </h3>
                          <div className="flex items-baseline gap-2">
                            <p className="text-sm font-bold text-gray-900">
                              {newProduct.price.toFixed(2)} zł
                            </p>
                            <span className="text-xs text-gray-500">
                              netto: {(newProduct.price * 0.77).toFixed(2)} zł
                            </span>
                          </div>
                        </div>
                      </a>
                    </motion.div>
                  ))
                ) : (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-pulse space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-12 bg-gray-200 rounded w-full"
                        ></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          <div className="col-span-12 lg:col-span-10">
            <div className="grid grid-cols-10 w-full gap-6">
              {/* Główna zawartość - zdjęcie produktu */}
              <motion.div
                variants={itemVariants}
                className="col-span-12 lg:col-span-5 relative h-fit"
              >
                <Swiper
                  spaceBetween={0}
                  slidesPerView={1}
                  navigation
                  pagination={{ clickable: true, dynamicBullets: true }}
                  zoom={{ maxRatio: 3 }}
                  loop
                  modules={[Navigation, Pagination, Zoom]}
                  className="aspect-square bg-gray-100 rounded-lg overflow-hidden"
                >
                  {product && (
                    <>
                      <SwiperSlide key="primary">
                        <div className="swiper-zoom-container">
                          <img
                            src={product.primaryImage}
                            alt={product.name}
                            loading="lazy"
                            className="swiper-lazy centered-image"
                          />
                          <div className="swiper-lazy-preloader"></div>
                        </div>
                      </SwiperSlide>

                      {product.secondaryImages.map((image) => (
                        <SwiperSlide key={image.id}>
                          <div className="swiper-zoom-container">
                            <img
                              src={image.image}
                              alt={`${product.name} - ${image.id}`}
                              loading="lazy"
                              className="swiper-lazy centered-image"
                            />
                            <div className="swiper-lazy-preloader"></div>
                          </div>
                        </SwiperSlide>
                      ))}
                    </>
                  )}
                </Swiper>
                <motion.div
                  className="absolute top-4 right-4 z-10 bg-white/80 backdrop-blur-sm rounded-full p-2"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <button
                    onClick={handleShare}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    title="Udostępnij produkt"
                  >
                    <ShareIcon className="h-5 w-5 text-gray-700" />
                  </button>
                </motion.div>
              </motion.div>

              {/* Informacje o produkcie */}
              <motion.div
                variants={itemVariants}
                className="col-span-12 lg:col-span-5 space-y-6 h-fit"
              >
                <div className="space-y-4">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {product.name}
                  </h1>
                  <p className="text-gray-500">
                    Numer seryjny:{' '}
                    <span className="font-medium">{product.serialNumber}</span>
                  </p>
                  <a
                    href={`/producent/${product.producer.name}`}
                    className="inline-flex items-center text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    <img
                      src={product.producer.image!}
                      alt={product.producer.name}
                      className="h-8 w-8 object-contain mr-2"
                    />
                    <span>{product.producer.name}</span>
                  </a>
                </div>

                {/* Status i dostawa */}
                <motion.div
                  className="bg-gray-50 rounded-xl p-6 space-y-4"
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  {cheapestDelivery ? (
                    <>
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircleOutlineIcon />
                        <span className="font-medium">Produkt dostępny</span>
                      </div>
                      <div className="space-y-2 text-gray-600">
                        <div className="flex items-center space-x-2">
                          <LocalShippingIcon className="text-gray-400" />
                          <span>Dostawa: 2-10 dni roboczych</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span>Od {cheapestDelivery.deliveryCost} zł</span>
                          <img
                            src={getIconForDeliveryMethod(
                              cheapestDelivery.deliveryMethod,
                            )}
                            alt={translateDevlieryMethod(
                              cheapestDelivery.deliveryMethod,
                            )}
                            className="h-6"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-red-600 font-medium">
                      Produkt niedostępny
                    </div>
                  )}
                </motion.div>

                {/* Cena i akcje */}
                <motion.div
                  className="bg-white rounded-xl border p-6 space-y-4"
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="space-y-1">
                    {product.discount &&
                    product.discount.isActive &&
                    product.lowestPriceLast30Days ? (
                      <>
                        <div className="flex items-center gap-3">
                          <div className="text-3xl font-bold text-gray-900">
                            {product.price.toFixed(2)} zł
                          </div>
                          <div className="text-lg text-gray-500 line-through">
                            {product.lowestPriceLast30Days.toFixed(2)} zł
                          </div>
                          <div className="text-sm font-semibold text-white bg-purple-600 px-2 py-1 rounded-full">
                            {product.discount.type === 'percentage'
                              ? `- ${product.discount.value}%`
                              : `- ${product.discount.value.toFixed(2)} zł`}
                          </div>
                        </div>
                        <div className="text-gray-500">
                          Netto: {(product.price * 0.77).toFixed(2)} zł
                        </div>

                        {/* Informacja o najniższej cenie z ostatnich 30 dni - wymagana przez polskie prawo */}
                        {product.lowestPriceLast30Days && (
                          <div className="mt-2 text-xs text-gray-600 bg-gray-100 p-2 rounded">
                            Najniższa cena z 30 dni przed obniżką:{' '}
                            <span className="font-medium">
                              {product.lowestPriceLast30Days.toFixed(2)} zł
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-3xl font-bold text-gray-900">
                          {product.price.toFixed(2)} zł
                        </div>
                        <div className="text-gray-500">
                          Netto: {(product.price * 0.77).toFixed(2)} zł
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex items-center space-x-4">
                    <motion.div
                      className="flex items-center bg-gray-100 rounded-full p-1"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <button
                        onClick={() =>
                          productsNumber > 1 && setProductsNumber((p) => p - 1)
                        }
                        className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        <RemoveIcon className="h-5 w-5" />
                      </button>
                      <span className="w-12 text-center font-medium">
                        {productsNumber}
                      </span>
                      <button
                        onClick={() =>
                          productsNumber < 99 && setProductsNumber((p) => p + 1)
                        }
                        className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        <AddIcon className="h-5 w-5" />
                      </button>
                    </motion.div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                  flex-1 flex items-center justify-center space-x-2 py-3 px-6 
                  bg-primary text-white rounded-full font-medium
                  hover:bg-background transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
                      onClick={() => {
                        addToShoppingList(product, productsNumber);
                        setIsModalVisible(true);
                      }}
                      disabled={!cheapestDelivery}
                    >
                      <ShoppingBagIcon className="h-5 w-5" />
                      <span>Do koszyka</span>
                    </motion.button>
                  </div>
                </motion.div>

                {/* Kontakt */}
                <motion.div
                  className="grid grid-cols-3 gap-4"
                  variants={itemVariants}
                >
                  {[
                    {
                      icon: EmailIcon,
                      label: 'Napisz',
                      href: `mailto:${config.contact.email}`,
                    },
                    {
                      icon: PhoneIcon,
                      label: 'Zadzwoń',
                      href: 'tel:+48604856094',
                    },
                    {
                      icon: QuestionAnswerIcon,
                      label: 'Zapytaj',
                      onClick: handleDialogOpen,
                    },
                  ].map((action, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {action.href ? (
                        <a
                          href={action.href}
                          className="flex flex-col items-center p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <action.icon className="h-6 w-6 text-gray-600" />
                          <span className="mt-2 text-sm text-gray-600">
                            {action.label}
                          </span>
                        </a>
                      ) : (
                        <button
                          onClick={action.onClick}
                          className="w-full flex flex-col items-center p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <action.icon className="h-6 w-6 text-gray-600" />
                          <span className="mt-2 text-sm text-gray-600">
                            {action.label}
                          </span>
                        </button>
                      )}
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>

            {/* Sekcje produktu - pełna szerokość */}
            <div className="w-full my-10">
              {/* Opis */}
              {product.description && (
                <motion.section
                  variants={itemVariants}
                  className="prose prose-lg max-w-none mb-12"
                >
                  <h2 className="text-2xl font-bold text-gray-900">
                    Opis produktu
                  </h2>
                  <ReadOnlyRichTextDisplay content={product.description} />
                </motion.section>
              )}

              {/* Parametry */}
              {product.parameterValues.length > 0 && (
                <motion.section
                  variants={itemVariants}
                  className="overflow-hidden mb-12"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Specyfikacja techniczna
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {product.parameterValues.map((parameter, index) => (
                      <motion.div
                        key={parameter.parameterId}
                        className="bg-gray-50 rounded-lg p-4"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <dt className="text-sm text-gray-500">
                          {parameter.parameter.name}
                        </dt>
                        <dd className="mt-1 text-base font-medium text-gray-900">
                          {parameter.value}
                        </dd>
                      </motion.div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Podobne produkty - dodajemy nową sekcję przed recenzjami */}
              {product && (
                <motion.div variants={itemVariants}>
                  <SuggestedProducts productId={product.id} />
                </motion.div>
              )}

              {/* Sekcja recenzji */}
              {product && (
                <motion.div variants={itemVariants}>
                  <ReviewSection productId={product.id} />
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Nowa sekcja nowości dla urządzeń mobilnych */}
        {newProducts && newProducts.length > 0 && (
          <motion.div variants={itemVariants} className="lg:hidden mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Nowości</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {newProducts.slice(0, 4).map((newProduct) => (
                <motion.div
                  key={newProduct.id}
                  whileHover={{ scale: 1.03, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all h-full"
                >
                  <a
                    href={`/produkt/${newProduct.slug}`}
                    className="h-full flex flex-col"
                  >
                    <div className="aspect-square bg-gray-50 p-2">
                      <img
                        src={newProduct.mobilePrimaryImage}
                        alt={newProduct.name}
                        className="w-full h-full object-contain mix-blend-multiply"
                      />
                    </div>
                    <div className="p-3 flex flex-col flex-grow">
                      <h3 className="text-sm font-medium text-gray-800 line-clamp-2 mb-2 hover:text-blue-600 transition-colors">
                        {newProduct.name}
                      </h3>
                      <div className="mt-auto">
                        <div className="flex items-baseline gap-2">
                          <p className="text-sm font-bold text-gray-900">
                            {newProduct.price.toFixed(2)} zł
                          </p>
                          <span className="text-xs text-gray-500">
                            netto: {(newProduct.price * 0.77).toFixed(2)} zł
                          </span>
                        </div>
                      </div>
                    </div>
                  </a>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Zachowujemy istniejące komponenty */}
        <ProductQuestionaire
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
          products={[
            {
              productId: product?.id!,
              quantity: 1,
            },
          ]}
          singleProduct={true}
        />
        {product && (
          <ProudctAddedToList
            isModalVisible={isModalVisible}
            setIsModalVisible={setIsModalVisible}
            product={product}
            quantity={productsNumber}
          />
        )}
      </motion.div>
      <Seo
        title={`${product.name}`}
        description={product.description || ''}
        image={product.mobilePrimaryImage}
        noindex={false}
        canonical={`https://www.${config.company.name}.pl/produkt/${product.slug}`}
        product={product}
      />
    </>
  );
}

export default ProductPage;
